import { configService } from './config';

type ThemeType = 'light' | 'dark' | 'system';
export type AccentColorType = 'blue' | 'indigo' | 'cyan' | 'green' | 'orange' | 'pink' | 'purple' | 'red';

// Cold modern color palette
const COLORS = {
  light: {
    bg: '#F8F9FB',
    text: '#1A1D23',
  },
  dark: {
    bg: '#0F1117',
    text: '#E4E5E9',
  },
};

// Accent color definitions
export const ACCENT_COLORS: Record<AccentColorType, { main: string; hover: string; zhName: string; zhTwName: string; enName: string }> = {
  blue:   { main: '#3B82F6', hover: '#60A5FA', zhName: '科技蓝', zhTwName: '科技藍', enName: 'Blue' },
  indigo: { main: '#6366F1', hover: '#818CF8', zhName: '靛蓝紫', zhTwName: '靛藍紫', enName: 'Indigo' },
  cyan:   { main: '#06B6D4', hover: '#22D3EE', zhName: '青色',   zhTwName: '青色',   enName: 'Cyan' },
  green:  { main: '#10B981', hover: '#34D399', zhName: '翠绿',   zhTwName: '翠綠',   enName: 'Green' },
  orange: { main: '#F97316', hover: '#FB923C', zhName: '活力橙', zhTwName: '活力橙', enName: 'Orange' },
  pink:   { main: '#EC4899', hover: '#F472B6', zhName: '粉红',   zhTwName: '粉紅',   enName: 'Pink' },
  purple: { main: '#A855F7', hover: '#C084FC', zhName: '紫罗兰', zhTwName: '紫羅蘭', enName: 'Purple' },
  red:    { main: '#EF4444', hover: '#F87171', zhName: '热情红', zhTwName: '熱情紅', enName: 'Red' },
};

class ThemeService {
  private mediaQuery: MediaQueryList | null = null;
  private currentTheme: ThemeType = 'system';
  private currentAccentColor: AccentColorType = 'blue';
  private appliedTheme: 'light' | 'dark' | null = null;
  private initialized = false;
  private mediaQueryListener: ((event: MediaQueryListEvent) => void) | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    }
  }

  // 初始化主题
  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    try {
      const config = configService.getConfig();
      this.setTheme(config.theme);
      this.setAccentColor(config.accentColor || 'blue');

      // 监听系统主题变化
      if (this.mediaQuery) {
        this.mediaQueryListener = (e) => {
          if (this.currentTheme === 'system') {
            this.applyTheme(e.matches ? 'dark' : 'light');
          }
        };
        this.mediaQuery.addEventListener('change', this.mediaQueryListener);
      }
    } catch (error) {
      console.error('Failed to initialize theme:', error);
      // 默认使用系统主题
      this.setTheme('system');
    }
  }

  // 设置主题
  setTheme(theme: ThemeType): void {
    const effectiveTheme = theme === 'system'
      ? (this.mediaQuery?.matches ? 'dark' : 'light')
      : theme;

    if (this.currentTheme === theme && this.appliedTheme === effectiveTheme) {
      return;
    }

    console.log(`Setting theme to: ${theme}`);
    this.currentTheme = theme;

    if (theme === 'system') {
      // 如果是系统主题，则根据系统设置应用
      console.log(`System theme detected, using: ${effectiveTheme}`);
    }

    // 直接应用指定主题
    this.applyTheme(effectiveTheme);
  }

  // 获取当前主题
  getTheme(): ThemeType {
    return this.currentTheme;
  }

  // 获取当前有效主题（实际应用的明/暗主题）
  getEffectiveTheme(): 'light' | 'dark' {
    if (this.currentTheme === 'system') {
      return this.mediaQuery?.matches ? 'dark' : 'light';
    }
    return this.currentTheme;
  }

  // 设置主题色
  setAccentColor(color: AccentColorType): void {
    this.currentAccentColor = color;
    const accent = ACCENT_COLORS[color];
    if (accent) {
      const root = document.documentElement;
      root.style.setProperty('--accent-color', accent.main);
      root.style.setProperty('--accent-color-hover', accent.hover);
    }
  }

  // 获取当前主题色
  getAccentColor(): AccentColorType {
    return this.currentAccentColor;
  }

  // 应用主题到DOM
  private applyTheme(theme: 'light' | 'dark'): void {
    // 避免重复应用相同主题
    if (this.appliedTheme === theme) {
      return;
    }

    console.log(`Applying theme: ${theme}`);
    this.appliedTheme = theme;
    const root = document.documentElement;
    const colors = COLORS[theme];

    if (theme === 'dark') {
      // Apply dark theme to HTML element (for Tailwind)
      root.classList.add('dark');
      root.classList.remove('light');

      // Make sure theme is consistent across entire DOM
      document.body.classList.add('dark');
      document.body.classList.remove('light');

      // Set background and text colors
      root.style.backgroundColor = colors.bg;
      document.body.style.backgroundColor = colors.bg;
      document.body.style.color = colors.text;
    } else {
      // Apply light theme to HTML element (for Tailwind)
      root.classList.remove('dark');
      root.classList.add('light');

      // Make sure theme is consistent across entire DOM
      document.body.classList.remove('dark');
      document.body.classList.add('light');

      // Set background and text colors
      root.style.backgroundColor = colors.bg;
      document.body.style.backgroundColor = colors.bg;
      document.body.style.color = colors.text;
    }

    // Update CSS variables for color transition animations
    root.style.setProperty('--theme-transition', 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease');
    document.body.style.transition = 'var(--theme-transition)';

    // Ensure #root element also gets the theme
    const rootElement = document.getElementById('root');
    if (rootElement) {
      if (theme === 'dark') {
        rootElement.classList.add('dark');
        rootElement.classList.remove('light');
        rootElement.style.backgroundColor = colors.bg;
      } else {
        rootElement.classList.remove('dark');
        rootElement.classList.add('light');
        rootElement.style.backgroundColor = colors.bg;
      }
    }
  }
}

export const themeService = new ThemeService();
